/**
 * The door: who is waiting, and how long they have been.
 *
 * The number this screen exists for is not the queue length — it is the wait
 * against the quote. A host says "about twenty minutes" and then has no way of
 * knowing, forty minutes later, that they are the ones who said it. A party
 * told twenty and seated in fifty does not come back, and nobody finds out.
 *
 * So a party over their quote is called out before they come to the desk to
 * ask, in weight and in words as well as in colour — this is read across a
 * lectern in a dim entrance, at a glance, by somebody holding menus.
 *
 * The list is in arrival order and nothing else. A two-top that came second
 * goes in before a six that came first because a two-top came free, and the
 * host makes that call — the screen's job is to show who is waiting and how
 * long, not to pretend there is a queue.
 */

import { useState } from 'react';

import {
  canOverride,
  useAddToWaitlist,
  useBookFromWaitlist,
  useBookingDay,
  useBookingSlots,
  useCloseWaitlistEntry,
  useEntitlements,
  useFloor,
  usePermission,
  useSeatFromWaitlist,
  useWaitlist,
  type FloorTable,
  type WaitingParty,
} from '@restaurant/api-client';
import {
  ActionError,
  Button,
  Empty,
  ErrorNotice,
  Field,
  Input,
  Loading,
  Sheet,
} from '@restaurant/ui';

export function Waitlist({ locationId }: { locationId: string | undefined }) {
  const waiting = useWaitlist(locationId);
  const [adding, setAdding] = useState(false);
  const [seating, setSeating] = useState<WaitingParty | null>(null);
  const [later, setLater] = useState<WaitingParty | null>(null);
  const entitlements = useEntitlements();
  // A party who will not wait can be given a table for later instead — the
  // door and the book are the same desk. Enterprise, like bookings.
  const mayWrite = usePermission('reservations.booking.write');
  const mayBook = entitlements.has('floor.reservations') && mayWrite;

  if (waiting.isPending) return <Loading label="Loading the door" />;
  if (waiting.isError) return <ErrorNotice message={waiting.error.message} />;

  const parties = waiting.data;
  const over = parties.filter((party) => party.over_quote).length;

  return (
    <div className="wl">
      <header className="wl__head">
        <div className="wl__count">
          <span className="wl__count-value">{parties.length}</span>
          <span className="wl__count-label">
            {parties.length === 1 ? 'party waiting' : 'parties waiting'}
          </span>
        </div>
        {/* Said at the top because it is the thing a host would otherwise
            find out from the party. */}
        {over > 0 && (
          <p className="wl__over-count">
            {over === 1
              ? '1 is past what they were told'
              : `${over} are past what they were told`}
          </p>
        )}
        <Button variant="primary" size="lg" onClick={() => setAdding(true)}>
          Add a party
        </Button>
      </header>

      {parties.length === 0 ? (
        <Empty
          title="Nobody waiting"
          description="Take a name at the door and it appears here, with how long they have been standing."
        />
      ) : (
        <ul className="wl__list">
          {parties.map((party) => (
            <li key={party.id}>
              <article className={`wl__party${party.over_quote ? ' wl__party--over' : ''}`}>
                <div className="wl__who">
                  <span className="wl__name">{party.name}</span>
                  <span className="wl__size">
                    {party.party_size} {party.party_size === 1 ? 'cover' : 'covers'}
                  </span>
                  {party.note && <span className="wl__note">{party.note}</span>}
                </div>

                <div className="wl__waited">
                  <span className="wl__waited-value tabular">{party.waited_minutes}m</span>
                  {/* The quote is only meaningful beside the wait, so it is
                      never shown anywhere else. */}
                  <span className="wl__quote">
                    {party.quoted_minutes === null
                      ? 'no quote given'
                      : party.over_quote
                        ? `told ${party.quoted_minutes}m — over`
                        : `told ${party.quoted_minutes}m`}
                  </span>
                </div>

                <div className="wl__act">
                  <Button variant="primary" onClick={() => setSeating(party)}>
                    Seat
                  </Button>
                  {mayBook && <Button onClick={() => setLater(party)}>Later</Button>}
                  <CloseParty party={party} />
                </div>
              </article>
            </li>
          ))}
        </ul>
      )}

      {adding && locationId && (
        <AddPartySheet locationId={locationId} onDone={() => setAdding(false)} />
      )}
      {later && locationId && (
        <BookForLaterSheet
          locationId={locationId}
          party={later}
          onDone={() => setLater(null)}
        />
      )}
      {seating && locationId && (
        <SeatPartySheet
          locationId={locationId}
          party={seating}
          onDone={() => setSeating(null)}
        />
      )}
    </div>
  );
}

/**
 * Gone, one way or the other.
 *
 * Two buttons rather than one, because the two are different facts: a party
 * who gave up after forty minutes is a table lost and a quote that was wrong,
 * and one who did not answer may be back in five minutes. A single "remove"
 * would throw away the only one worth acting on.
 */
function CloseParty({ party }: { party: WaitingParty }) {
  const close = useCloseWaitlistEntry();
  const [asking, setAsking] = useState(false);

  if (!asking) {
    return (
      <Button onClick={() => setAsking(true)} aria-label={`Take ${party.name} off the list`}>
        Gone
      </Button>
    );
  }

  return (
    <div className="wl__gone">
      <Button
        loading={close.isPending}
        onClick={() => close.mutate({ entryId: party.id, noShow: false })}
      >
        Gave up
      </Button>
      <Button
        loading={close.isPending}
        onClick={() => close.mutate({ entryId: party.id, noShow: true })}
      >
        No answer
      </Button>
      <Button variant="ghost" onClick={() => setAsking(false)}>
        Cancel
      </Button>
    </div>
  );
}

function AddPartySheet({ locationId, onDone }: { locationId: string; onDone: () => void }) {
  const add = useAddToWaitlist();
  const [name, setName] = useState('');
  const [size, setSize] = useState('2');
  const [quote, setQuote] = useState('');
  const [note, setNote] = useState('');

  return (
    <Sheet
      title="Add a party"
      onDismiss={onDone}
      actions={
        <Button
          variant="primary"
          size="lg"
          block
          disabled={name.trim() === '' || Number(size) < 1}
          loading={add.isPending}
          onClick={() =>
            add.mutate(
              {
                locationId,
                name: name.trim(),
                partySize: Number(size),
                ...(quote.trim() ? { quotedMinutes: Number(quote) } : {}),
                ...(note.trim() ? { note: note.trim() } : {}),
              },
              { onSuccess: onDone },
            )
          }
        >
          Add them
        </Button>
      }
    >
      <Field label="Name" hint="What you will call out.">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Okonkwo" />
      </Field>

      <Field label="Covers">
        <Input
          type="number"
          inputMode="numeric"
          value={size}
          onChange={(e) => setSize(e.target.value)}
        />
      </Field>

      <Field
        label="Told them"
        hint="In minutes. Leave blank if nobody promised anything — a guess here would be reported back to you as a broken promise."
      >
        <Input
          type="number"
          inputMode="numeric"
          value={quote}
          onChange={(e) => setQuote(e.target.value)}
          placeholder="20"
        />
      </Field>

      <Field label="Anything else" hint="High chair, by the window. Optional.">
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="High chair"
        />
      </Field>

      <ActionError error={add.error} action="Adding the party" />
    </Sheet>
  );
}

/**
 * Which table they are going to.
 *
 * Free tables first and big enough ones first within that: a host scanning
 * this has already decided the party fits, and the list should not make them
 * check. Tables too small are still offered — pushing two together and seating
 * six on a four is ordinary — but they are said to be small rather than
 * silently mixed in.
 */
function SeatPartySheet({
  locationId,
  party,
  onDone,
}: {
  locationId: string;
  party: WaitingParty;
  onDone: () => void;
}) {
  const floor = useFloor(locationId);
  const seat = useSeatFromWaitlist();

  const tables: FloorTable[] = (floor.data ?? [])
    .flatMap((plan) => plan.tables)
    .filter((table) => table.can_seat)
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

  return (
    <Sheet title={`Seat ${party.name}`} onDismiss={onDone}>
      <p className="wl__sheet-note">
        {party.party_size} {party.party_size === 1 ? 'cover' : 'covers'}, waiting{' '}
        {party.waited_minutes} minutes.
      </p>

      {floor.isPending && <Loading label="Loading the room" />}
      {floor.isError && <ErrorNotice message={floor.error.message} />}

      {floor.data && tables.length === 0 && (
        <p className="wl__sheet-note">
          Nothing free. A table has to be cleared before anybody can sit at it.
        </p>
      )}

      <ul className="wl__tables">
        {tables.map((table) => (
          <li key={table.id}>
            <button
              type="button"
              className="wl__table"
              disabled={seat.isPending}
              onClick={() =>
                seat.mutate({ entryId: party.id, tableId: table.id }, { onSuccess: onDone })
              }
            >
              <span className="wl__table-label">Table {table.label}</span>
              <span className="wl__table-seats">
                {table.combined_seats} {table.combined_seats === 1 ? 'seat' : 'seats'}
                {table.combined_seats < party.party_size && ' — small for them'}
              </span>
            </button>
          </li>
        ))}
      </ul>

      <ActionError error={seat.error} action="Seating them" />
    </Sheet>
  );
}

/**
 * The wait is too long, so they take a table for later instead.
 *
 * Their name, number and size come from the list; they come off it as
 * booked, which is neither a table lost nor a party seated. The times on
 * offer are the site's, today first.
 */
function BookForLaterSheet({
  locationId,
  party,
  onDone,
}: {
  locationId: string;
  party: WaitingParty;
  onDone: () => void;
}) {
  const today = useBookingDay(locationId);
  const [date, setDate] = useState<string | null>(null);
  const [time, setTime] = useState('');
  const [email, setEmail] = useState('');
  const day = date ?? today.data?.business_date ?? '';
  const slots = useBookingSlots(locationId, day, party.party_size);
  const book = useBookFromWaitlist();

  function send(override: boolean) {
    book.mutate(
      {
        entryId: party.id,
        date: day,
        time,
        ...(email.trim() ? { email: email.trim() } : {}),
        override,
      },
      { onSuccess: onDone },
    );
  }

  return (
    <Sheet
      title={`A table later for ${party.name}`}
      onDismiss={onDone}
      actions={
        <>
          {canOverride(book.error) && (
            <Button size="lg" block loading={book.isPending} onClick={() => send(true)}>
              Book anyway
            </Button>
          )}
          <Button
            variant="primary"
            size="lg"
            block
            disabled={!day || !time}
            loading={book.isPending}
            onClick={() => send(false)}
          >
            Book {time ? `for ${time}` : ''}
          </Button>
        </>
      }
    >
      <p className="wl__sheet-note">
        {party.party_size} {party.party_size === 1 ? 'cover' : 'covers'}
        {party.phone ? ` · ${party.phone}` : ''}
      </p>
      <Field label="Day">
        <Input type="date" value={day} onChange={(e) => setDate(e.target.value)} />
      </Field>
      {slots.isPending && day ? (
        <Loading label="Looking at the day" />
      ) : (
        <ul className="wl__tables">
          {(slots.data ?? []).map((slot) => (
            <li key={slot.starts_at}>
              <button
                type="button"
                className="wl__table"
                aria-pressed={slot.local_time === time}
                onClick={() => setTime(slot.local_time)}
              >
                <span className="wl__table-label">
                  {slot.local_time}
                  {slot.local_time === time && ' — picked'}
                </span>
                <span className="wl__table-seats">
                  {slot.full
                    ? 'full'
                    : slot.table_label
                      ? `table ${slot.table_label}`
                      : 'no single table free'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <Field label="Email" hint="Optional. The confirmation goes here.">
        <Input
          value={email}
          type="email"
          maxLength={254}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      <ActionError error={book.error} action="Booking them" title="Not booked" />
    </Sheet>
  );
}
